// moment-timezone (not plain 'moment') so a test's / app's `moment.tz.setDefault`
// applies to these formatters too — matches the rest of the RecoveryAI slice.
import moment from 'moment-timezone'

// Shared pure date/time formatters (RecoveryAI case detail, the shared
// Transcript, and the Reports order-rating detail page). Each returns the
// formatted string, or '' for a falsy/unparseable timestamp — callers decide how
// to render the empty case (index.tsx shows an em-dash via t('empty')/raw-iso;
// Transcript hides the label entirely). moment(undefined) is "valid" and equals
// NOW, so the leading falsy guard is load-bearing: it stops a missing timestamp
// from rendering the current time.
const format = (iso: string | null | undefined, pattern: string): string => {
  if (!iso) return ''
  const parsed = moment(iso)
  return parsed.isValid() ? parsed.format(pattern) : ''
}

// Time-of-day, e.g. "1:20 PM" (no tz suffix).
export const formatTime = (iso?: string | null): string => format(iso, 'h:mm A')

// Date-only, e.g. "Mar 8, 2026".
export const formatDate = (iso?: string | null): string =>
  format(iso, 'MMM D, YYYY')

// Short date-only, e.g. "09/04" — the Unified Feedback Inbox list row's
// date column (docs/06-ux-rules.md "List" / "Row anatomy": "date (MM/DD)";
// prototype/v69's `ibShortDate()`). Distinct from `formatDate` above, which
// is the long form used elsewhere (RecoveryAI case detail, Reports).
export const formatShortDate = (iso?: string | null): string =>
  format(iso, 'MM/DD')

// Date + time, e.g. "Mar 8, 2026 1:20 PM".
export const formatDateTime = (iso?: string | null): string =>
  format(iso, 'MMM D, YYYY h:mm A')

// Short date + time, e.g. "Sep 3, 5:20 PM" — no year. The Unified Feedback
// Inbox thread pane's timestamp format for every item type (guest message,
// chat bubble, system note, email card; docs/06-ux-rules.md "Thread";
// prototype/v70's `fmtDate()` + `fmt()`, VISUAL-SPEC.md §1). Distinct from
// `formatDateTime` above, which is the long form (with year) used
// elsewhere.
export const formatShortDateTime = (iso?: string | null): string =>
  format(iso, 'MMM D, h:mm A')

// Numeric date + time, e.g. "09/08/2026 5:20 PM" — the Unified Feedback
// Inbox right rail's "Date & time" row (VISUAL-SPEC.md §4 "Order Info tab").
export const formatNumericDateTime = (iso?: string | null): string =>
  format(iso, 'MM/DD/YYYY h:mm A')

// Numeric date-only, e.g. "03/02/2026" — the Unified Feedback Inbox right
// rail's Recent feedback tab per-row date (VISUAL-SPEC.md §4 "Recent
// feedback tab").
export const formatNumericDate = (iso?: string | null): string =>
  format(iso, 'MM/DD/YYYY')
